import React, {
    useContext, useState, useCallback, useMemo, useEffect
} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withRouter } from 'react-router-dom';
import { InlineLoader, Tooltip } from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { pageTemplateProps, WizardContext } from '@xengage/gw-portals-wizard-react';
import { MLAppStateContext, MLNotificationBlockContext, MLNotificationBlockUtil } from 'ml-portals-util-react';
import { MLTagManagerService } from 'ml-portals-services-react'
import { MLAlertGroupComponent } from 'ml-components-common-react';

import messages from './DigitalAMPWizardPageTemplate.messages';

import styles from './DigitalWizardPageTemplate.module.scss';
import AMPWizardSidebar from '../components/AMPWizardSidebar';
import { Button } from '@jutro/legacy/components';

import { BreakpointTrackerContext } from '@jutro/legacy/layout';

function renderLoader(isLoading) {
    return isLoading ? <InlineLoader loading={isLoading} className="gw-inline-loader" /> : null;
}

function DigitalAMPWizardPageTemplate(props) {
    const translator = useTranslator();
    const breakpoint = useContext(BreakpointTrackerContext);
    const { setAppState } = useContext(MLAppStateContext);
    const isPhone = breakpoint === 'phoneWide' || breakpoint === 'phone';
    const {
        children: content,
        onNext,
        isLoadingNext,
        disableNext,
        showNext,
        nextLabel,
        nextButtonTooltip,
        onPrevious,
        isLoadingPrevious,
        disablePrevious,
        showPrevious,
        previousLabel,
        onCancel,
        isLoadingCancel,
        disableCancel,
        showCancel,
        cancelLabel,
        renderContextComponent,
        renderWidgets,
        wizardData,
        wizardSnapshot,
        showContextComponent,
        showWizardHeader,
        customMessageModal,
        message,
        showPrivacyPolicy,
        renderPrivacyPolicyComponent,
        showTextNextToButton,
        textNextToButton,
        history,
        location
    } = props;
    const wizardContext = useContext(WizardContext);
    const {
        currentStep
    } = wizardContext;
    const { addWarningAlert, addErrorAlert, clearBlockAlerts} = MLNotificationBlockUtil();

    useEffect(() => {
        setAppState({
            quoteID: _.get(wizardData, 'quoteID.value'),
            sessionUUID: _.get(wizardData, 'sessionUUID.value'),
            emailAddress1: _.get(wizardData, 'baseData.accountHolder.emailAddress1.value'),
            phoneNumber: _.get(wizardData, 'baseData.accountHolder.homeNumber.value'),
            submissionStatus: _.get(wizardData, 'baseData.status_ML.value'),
            policyNumber: _.get(wizardData, 'bindData.policyNumber.value')
        });
        MLTagManagerService.setModel(_.get(wizardData, 'submissionVM.value') || _.get(wizardData, 'value'));
    }, [setAppState, wizardData])

    const contextComponent = useCallback(() => {
        return <div className={styles.wizardContext}>{renderContextComponent(wizardSnapshot)}</div>;
    }, [renderContextComponent, wizardSnapshot]);

    const shouldShowButtons = useMemo(() => !(
        showNext === false
        && showPrevious === false
        && showCancel === false
    ), [showCancel, showNext, showPrevious]);

    const isPerformingTransiton = useMemo(() => (
        isLoadingCancel || isLoadingPrevious || isLoadingNext
    ), [isLoadingCancel, isLoadingNext, isLoadingPrevious]);

    const shouldShowMessage = useMemo(() => {
        return !_.isEmpty(message);
    }, [message]);

    const nextButton = useMemo(() => (
        <Button
            id="gw-wizard-Next"
            className={styles.nextbutton}
            disabled={disableNext || isPerformingTransiton}
            onClick={onNext}
        >
            {translator(nextLabel)}
            {renderLoader(isLoadingNext)}
        </Button>
    ), [disableNext, isLoadingNext, isPerformingTransiton, nextLabel, onNext, translator]);

    const nextButtonWithTooltip = useMemo(() => (
        <Tooltip id="digitalWizardNextButtonTooltip" placement="bottom" content={nextButtonTooltip}>
            <span>{nextButton}</span>
        </Tooltip>
    ), [nextButton, nextButtonTooltip]);

    const renderNextButton = nextButtonTooltip ? nextButtonWithTooltip : nextButton;
    const buttonContainerClassName = classNames(styles.navTransitionButtons, {
        [styles.navTransitionButtonOnlyNext]: !showPrevious && isPhone
    });

    const { notificationBlock } = useContext(MLNotificationBlockContext);
    const hasAlerts = notificationBlock && notificationBlock.length !== 0

    const shouldHideRightRail = () => {
        return currentStep.mlStepName === 'endorsementQuoteDetails' || currentStep.mlStepName === 'endorsementESignature';
    };

    // For Policy Change, wizard data is a wrapper around subVM
    let submissionVM = wizardData;
    if (_.has(wizardData, 'submissionVM')) {
        submissionVM = wizardData.submissionVM;
    };

    const showRenewalMessage = () => {
        return submissionVM.hasFutureRenewals_ML && submissionVM.hasFutureRenewals_ML.value && submissionVM.changesAppliedForward && !submissionVM.changesAppliedForward.value;
    };

    const showSuspendLiabilityMessage = () => {
        return submissionVM.isSuspendedLiabilitySelected;
    };

    const onConfirmationScreen = () => {
        return currentStep.mlStepName === 'endorsementConfirmation';
    };

    const onCoverageScreen = () => {
        return currentStep.mlStepName === 'endorsementCoverages';
    };

    const changeProcessedAlert = () => {
        const successMsg = translator(messages.confirmationSuccess);
        const errorMsg = translator(messages.confirmationError);
        if (onConfirmationScreen() && !showRenewalMessage()) {
            addWarningAlert(successMsg, 'policyChangeProcessed');
        } else if (onConfirmationScreen() && showRenewalMessage()) {
            addErrorAlert(errorMsg, 'policyChangeProcessed');
        } else {
            clearBlockAlerts('policyChangeProcessed');
        }
    };

    const suspendLiablityCoverageAlert = () => {
        const errorMsg1 = translator(messages.liabilityWarning1);
        const errorMsg2 = translator(messages.liabilityWarning2);
        if (onCoverageScreen() && showSuspendLiabilityMessage()) {
            const warningMsg = `<p>${errorMsg1}</p><p>${errorMsg2}</p>`;
            addWarningAlert(warningMsg, 'suspendedLiabilityMessage');
        } else {
            clearBlockAlerts('suspendedLiabilityMessage');
        }
    };

    useEffect(() => {
        suspendLiablityCoverageAlert();
        if(onConfirmationScreen()) {
            changeProcessedAlert()
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const goToClaimDetails = () => {
        history.push(`/claims/${_.get(wizardData, 'claimNumber.value')}`)
    }

    const goToAccountHome = () => {
        history.push('/home');
    }

    const wizardPageClassName = hasAlerts ? 'container' : 'container containerPadding';
    let wizardContentColumnClassName; 
    let rightRailColumnClassName;
    const isFNOL = location.pathname.startsWith("/fnol")
    if(isFNOL){
        wizardContentColumnClassName = shouldHideRightRail() ? "col-md-12" :"col-md-9";
        rightRailColumnClassName = "col-md-3"
    }else{
        wizardContentColumnClassName = shouldHideRightRail() ? "col-md-12" :"col-md-8";
        rightRailColumnClassName = "col-md-4"
    }
    const isFNOLConfirmationPage = isFNOL && currentStep.id === "confirmation";
    return (
        <>
            <div className="row clearfix">
                {showContextComponent && contextComponent()}           
                {hasAlerts && <div className={styles.mlAlertGroupComponent}><MLAlertGroupComponent /></div>}
            </div>           
            <div className={wizardPageClassName}>
                <div className="row clearfix">
                    <div className={wizardContentColumnClassName}>
                        <div>
                            {showWizardHeader && contextComponent()}
                            {content}
                            <div className={classNames({ [styles.navButtons]: shouldShowButtons }, 'hiddenPrintElement')}>
                                {showCancel && (
                                    <Button
                                        id="gw-wizard-cancel"
                                        disabled={disableCancel || isPerformingTransiton}
                                        type="text"
                                        onClick={onCancel}
                                    >
                                        {translator(cancelLabel)}
                                        {renderLoader(isLoadingCancel)}
                                    </Button>
                                )}
                                <div className={buttonContainerClassName}>
                                    {showPrevious && (
                                        <Button
                                            id="gw-wizard-previous"
                                            disabled={disablePrevious || isPerformingTransiton}
                                            type="outlined"
                                            onClick={onPrevious}
                                        >
                                            {translator(previousLabel)}
                                            {renderLoader(isLoadingPrevious)}
                                        </Button>
                                    )}
                                    {showNext && renderNextButton}
                                    {showTextNextToButton
                                    && (
                                        <div className={styles.mlTextNextToButton}>
                                            <span className="ml-cta-sub-footer__content">{translator(textNextToButton)}</span>
                                        </div>
                                    )}
                                </div>
                                {shouldShowMessage && (
                                    <span className="ml-cta-sub-footer__content">{translator(message)}</span>
                                ) }
                            </div>
                        </div>
                    </div>
                    {!shouldHideRightRail() && (
                        <div className={isFNOL ? 'no-gutter' : undefined}>
                            <div className={rightRailColumnClassName}>
                                <AMPWizardSidebar customMessageModal={customMessageModal} />
                            </div>
                        </div>
                    )}
                </div>
                { isFNOLConfirmationPage && 
                    <div className="ml-cta-sub-footer">
                        <p>{translator(messages.fnolConfirmationFooterText)}</p>
                        <Button type="filled" onClick={goToClaimDetails}>
                            {translator(messages.ClaimDetails)}
                        </Button>
                        <Button type="outlined" onClick={goToAccountHome}>
                            {translator(messages.ReturnToYourAccount)}
                        </Button>
                    </div>
                }
            </div>
        </>
    );
}

DigitalAMPWizardPageTemplate.propTypes = {
    ...pageTemplateProps,
    /** a function that accepts wizardData to render a React component */
    renderContextComponent: PropTypes.func,
    /** a function that accepts wizardData to render a React component */
    renderWidgets: PropTypes.func,
    showContextComponent: PropTypes.bool,
    showWizardHeader: PropTypes.bool,
    nextButtonTooltip: PropTypes.string
};

DigitalAMPWizardPageTemplate.defaultProps = {
    renderContextComponent: () => null,
    renderWidgets: () => null,
    showContextComponent: false,
    showWizardHeader: true,
    nextButtonTooltip: ''
};

export default withRouter(DigitalAMPWizardPageTemplate);
